import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "technical-documentation",
  kind: "horizontal",
  title: "Technical Documentation & Engineering Drawings",
  shortTitle: "Documentation",
  status: "draft",
  tier: "we-do",
  seoTitle: "Technical Documentation & Engineering Drawings | GA",
  seoDescription:
    "Controlled engineering records, document numbering hierarchies, AutoCAD drafting management, P&IDs, wiring schematics, and operational procedures.",
  summary:
    "Authoritative documentation architectures, document numbering systems, AutoCAD drafting management, P&IDs, wiring schematics, and operating procedures that bring clarity to critical facilities.",
  image: "/images/blog/s1000d-issue-archaeology-hero.webp",
  relatedCapabilitySlugs: [
    "data-search-knowledge",
    "software-architecture-engineering",
  ],
  relatedIndustrySlugs: ["energy", "enterprise", "military"],
  relatedPostSlugs: [
    "engineering-document-numbering-systems",
    "s1000d-issue-archaeology",
    "schemas-first-parsers-last",
    "interactive-wiring-and-pids",
  ],
  engineering: [
    "Standardized engineering document numbering systems, equipment tag conventions (KKS, ISA), and revision control",
    "Piping & Instrumentation Diagrams (P&IDs), electrical single-line diagrams, and schematic wiring package management",
    "Specialist AutoCAD and MicroStation drafting subcontracting, scope definition, layer standards, and quality reviews",
    "Physical redline walkdowns and field verification on running production and generating units",
    "Standard operating procedures (SOPs), maintenance work instructions, and lockout/tagout (LOTO) procedures",
    "Controlled electronic document management systems (EDMS) and searchable metadata cross-referencing",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Documentation Is an Operating System",
    },
    {
      type: "paragraph",
      text: "A procedure, guideline, P&ID, electrical one-line diagram, or work package is useful only when people can tell what it governs, who owns it, which version applies, and what should happen next. Technical documentation is not a formatting task. It is a system for connecting physical equipment, operating decisions, engineering changes, and compliance records without leaving critical context in memory or email.",
    },
    {
      type: "heading",
      level: 2,
      text: "Standardized Numbering and Drawing Control",
    },
    {
      type: "paragraph",
      text: "Operating facilities accumulate decades of contradictory CAD drawings, scanned prints, and ambiguous filenames. When an outage crew pulls a drawing during emergency maintenance, a mislabeled revision or mismatched equipment tag causes immediate delays and safety risks. We establish clear document numbering schemas—grounded in standards like KKS, ISA-5.1, or client-specific conventions—ensuring every drawing, specification, and procedure has an unambiguous place in the plant hierarchy.",
    },
    {
      type: "heading",
      level: 2,
      text: "Managing CAD Drafting and Subcontractor Packages",
    },
    {
      type: "paragraph",
      text: "When formal engineering drawings, P&ID updates, or electrical schematic redraws are required, General Applications manages the technical scope, field redline walkdowns, drafting seed templates, and acceptance criteria. We subcontract and coordinate directly with specialist drafters, reviewing every CAD submittal for layer hygiene, symbol accuracy, and title block compliance before drawings are stamped and issued into document control.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit existing drawings, procedures, naming conventions, and physical plant records to identify gaps, conflicting revisions, and drafting requirements.",
      produces: [
        "Drawing and technical record inventory with revision status",
        "Document numbering schema and taxonomy gap analysis",
        "Physical walkdown and field redline scoping plan",
        "CAD drafting subcontractor scope and resource requirements",
      ],
      fit: {
        good: [
          "Operating plants and critical facilities with disorganized, unindexed, or out-of-date drawing archives",
          "Capital engineering and retrofit projects requiring a rigorous document numbering schema and drafting coordination",
        ],
        poor: [
          "Organizations seeking cosmetic copywriting without operational, technical, or engineering grounding",
          "Requests for licensed PE-stamped structural designs without designated design authority",
        ],
      },
    },
    "initial-design": {
      focus:
        "Establish the document numbering hierarchy, CAD standards, review workflows, and drafting templates.",
      produces: [
        "Authoritative document numbering and equipment tagging specification",
        "AutoCAD/MicroStation seed templates, layer standards, and title block configurations",
        "Engineering change order (ECO) and revision approval workflows",
        "Drafter subcontracting agreements, milestones, and deliverable schedules",
      ],
    },
    "detailed-design": {
      focus:
        "Execute field redline walkdowns, draft updated CAD drawings, and author controlled procedures against approved standards.",
      produces: [
        "Field-verified redlines for P&IDs, one-lines, and control wiring schematics",
        "Clean, layer-compliant CAD drawing packages produced by specialist drafters",
        "Standardized operating procedures, test protocols, and maintenance work instructions",
        "Cross-referenced equipment tag indices linking drawings to physical assets",
      ],
    },
    implementation: {
      focus:
        "Publish controlled drawing sets into the client's electronic document management system (EDMS) and train staff on change procedures.",
      produces: [
        "Controlled, release-ready drawing repository with hyperlinked navigation",
        "As-built drawing packages delivered to document control and operations",
        "Drawing checkout, revision reservation, and drafting maintenance handbook",
        "Training and handover for plant engineering and document control staff",
      ],
    },
  },
};
